import { useEffect, useState } from 'react'
import { LayoutGroup, motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { Home, Search, ShoppingBag, Heart, Package } from 'lucide-react'
import { StoreScrollLink } from '@/store/components/StoreScrollLink'
import { useCartStore } from '@/store/stores/cartStore'
import { useWishlistStore } from '@/store/stores/wishlistStore'
import { cn } from '@/lib/utils'
import { scrollToTop } from '@/lib/scrollToTop'
import { storeBottomNavState, markStoreTabFromBottomNav } from '@/store/lib/storeBackNav'
import {
  prefetchStoreBottomNavRoutes,
  prefetchStoreTabRoute,
} from '@/store/lib/prefetchStoreRoutes'
import { getStoreNavActive } from './storeNav'

const tabs = [
  {
    to: '/',
    label: 'Home',
    icon: Home,
    isActive: (a: ReturnType<typeof getStoreNavActive>) => a.isHome,
  },
  {
    to: '/search',
    label: 'Search',
    icon: Search,
    isActive: (a: ReturnType<typeof getStoreNavActive>) => a.isSearch,
  },
  {
    to: '/orders',
    label: 'Orders',
    icon: Package,
    isActive: (a: ReturnType<typeof getStoreNavActive>) => a.isOrders,
  },
  {
    to: '/wishlist',
    label: 'Wishlist',
    icon: Heart,
    isActive: (a: ReturnType<typeof getStoreNavActive>) => a.isWishlist,
    filledWhenActive: true as const,
  },
  {
    to: '/cart',
    label: 'Cart',
    icon: ShoppingBag,
    isActive: (a: ReturnType<typeof getStoreNavActive>) => a.isCart,
  },
] as const

export function StoreBottomNav() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [optimisticPath, setOptimisticPath] = useState<string | null>(null)
  const active = getStoreNavActive(optimisticPath ?? pathname)
  const itemCount = useCartStore((s) => s.itemCount())
  const wishCount = useWishlistStore((s) => s.ids.length)

  useEffect(() => {
    setOptimisticPath(null)
  }, [pathname])

  useEffect(() => {
    const prefetch = () => prefetchStoreBottomNavRoutes()

    if (typeof requestIdleCallback !== 'undefined') {
      const id = requestIdleCallback(prefetch, { timeout: 2000 })
      return () => cancelIdleCallback(id)
    }

    const timer = window.setTimeout(prefetch, 300)
    return () => window.clearTimeout(timer)
  }, [])

  return (
    <nav className="store-bottom-nav" aria-label="Primary">
      <div className="store-bottom-nav-backdrop" aria-hidden />
      <div className="store-bottom-nav-shell">
        <LayoutGroup id="store-bottom-nav">
          <div className="store-bottom-nav-inner">
            {tabs.map((tab) => {
              const { to, label, icon: Icon, isActive } = tab
              const current = isActive(active)
              const filledWhenActive =
                'filledWhenActive' in tab && tab.filledWhenActive
              const badge =
                to === '/cart' ? itemCount : to === '/wishlist' ? wishCount : 0

              return (
                <StoreScrollLink
                  key={to}
                  to={to}
                  state={storeBottomNavState()}
                  aria-current={current ? 'page' : undefined}
                  onPointerDown={() => prefetchStoreTabRoute(to)}
                  onClick={(e) => {
                    markStoreTabFromBottomNav(to)

                    if (current) {
                      e.preventDefault()
                      navigate(to, { state: storeBottomNavState(), replace: true })
                      scrollToTop()
                      return
                    }

                    setOptimisticPath(to)
                  }}
                  className={cn(
                    'store-bottom-nav-item',
                    current && 'store-bottom-nav-item--active'
                  )}
                >
                  <span className="store-bottom-nav-item-icon">
                    {current && (
                      <motion.span
                        layoutId="store-bottom-nav-active"
                        className="store-bottom-nav-active-pill"
                        transition={{
                          type: 'spring',
                          stiffness: 520,
                          damping: 36,
                        }}
                      />
                    )}
                    <Icon
                      className={cn(
                        'relative z-10 h-[1.125rem] w-[1.125rem]',
                        filledWhenActive &&
                          current &&
                          'fill-gold-600 text-gold-600'
                      )}
                      strokeWidth={current ? 2.25 : 2}
                      aria-hidden
                    />
                    {badge > 0 && (
                      <span
                        className={cn(
                          'store-bottom-nav-badge',
                          to === '/cart' && 'store-bottom-nav-badge--cart'
                        )}
                      >
                        {badge > 9 ? '9+' : badge}
                      </span>
                    )}
                  </span>
                  <span className="store-bottom-nav-label">{label}</span>
                </StoreScrollLink>
              )
            })}
          </div>
        </LayoutGroup>
      </div>
    </nav>
  )
}
